import * as Yup from "yup";
import { getCustomFieldValidations } from "./customYupSchemas";
import { evaluateRuleById } from "./utils";
import { returnCustomFieldsFromSchema } from "../../../../services/custom-fields";
const getRequiredVisibleFieldsFromSchema = (schema, rules, formData, preview) => {
  const requiredFields = [];
  const evaluateVisibility = (field) => {
    const { visible, visible_check } = field;
    const handlerResult = visible_check?.handler ? evaluateRuleById(visible_check.handler, formData, rules, preview) : void 0;
    if (!visible_check?.handler) {
      return visible !== false;
    }
    if (visible === true && handlerResult === true) return false;
    if (visible === true && handlerResult === false) return true;
    if (visible === false && handlerResult === true) return true;
    return false;
  };
  const screens = schema.schema.screens[0].screens;
  screens.forEach((screen) => {
    ["VISIBLE", "DETAILED"].forEach((sectionName) => {
      const section = schema.schema.screens.find((s) => s.id === screen.id)?.fields?.find((f) => f.name === sectionName)?.fields ?? [];
      section.forEach((field) => {
        const path = field?.encompass_ids?.[0]?.modelPath;
        const isVisible = evaluateVisibility(field);
        const isRequired = field.required;
        const isDisabled = field.disabled;
        if (isVisible && isRequired && !isDisabled) {
          requiredFields.push({ path, label: field.label || path });
        }
      });
    });
  });
  return requiredFields;
};
const setInShape = (obj, pathParts, schema) => {
  const key = pathParts[0];
  if (pathParts.length === 1) {
    obj[key] = schema;
    return;
  }
  if (!obj[key]) {
    obj[key] = {};
  }
  setInShape(obj[key], pathParts.slice(1), schema);
};
const applyRequiredToCustomValidations = (customFieldValidations, requiredFields) => {
  requiredFields.forEach((requiredField) => {
    const { path, label } = requiredField;
    const customValidation = customFieldValidations.find(
      (validation) => validation.path === path
    );
    if (customValidation) {
      customValidation.validation = customValidation.validation.concat(
        Yup.mixed().nullable().required(`${label} is required`)
      );
    } else {
      customFieldValidations.push({
        path,
        label,
        validation: Yup.mixed().nullable().required(`${label} is required`)
      });
    }
  });
  return customFieldValidations;
};
const generateSubmissionSchema = (schema, preview, formValues) => {
  const rawShape = {};
  const customFieldValidations = getCustomFieldValidations(formValues);
  const requiredFields = getRequiredVisibleFieldsFromSchema(
    schema,
    schema?.schemaRules,
    formValues(),
    preview
  );
  const allValidations = applyRequiredToCustomValidations(
    customFieldValidations,
    requiredFields
  );
  for (const { path, validation } of allValidations) {
    const pathParts = path.split(".");
    setInShape(rawShape, pathParts, validation);
  }
  const toYupShape = (obj) => Yup.object(
    Object.fromEntries(
      Object.entries(obj).map(([key, val]) => [
        key,
        Yup.isSchema(val) ? val : toYupShape(val)
      ])
    )
  );
  return toYupShape(rawShape);
};
const validateAndSubmitSearchForm = async ({
  schemaForm,
  schema,
  preview = false
}) => {
  const formData = schemaForm.getValues();
  const yupSchema = generateSubmissionSchema(
    schema,
    preview,
    schemaForm.getValues
  );
  schemaForm.clearErrors();
  try {
    await yupSchema.validate(formData, { abortEarly: false });
    transformFormDataCustomFieldsToResponseFormData(
      formData,
      schema.schema.screens
    );
    return { valid: true, formData, errorPaths: [] };
  } catch (validationError) {
    const errors = validationError.inner || [];
    const errorMap = errors.reduce((acc, curr) => {
      acc[curr.path] = { message: curr.message };
      return acc;
    }, {});
    Object.entries(errorMap).forEach(([field, error]) => {
      schemaForm.setError(field, {
        type: "manual",
        message: error.message,
        shouldFocus: true
      });
    });
    return {
      valid: false,
      formData,
      errorPaths: Object.keys(errorMap)
    };
  }
};
const transformFormDataCustomFieldsToResponseFormData = (formData, screens) => {
  const flattenedCustomFields = returnCustomFieldsFromSchema(screens);
  const customFieldValues = formData.unmappedLockRequestAdditionalFields ?? null;
  if (customFieldValues) {
    const lockRequestAdditionalFields = Object.keys(customFieldValues).filter((key) => customFieldValues[key] !== void 0).map((key) => {
      const untransformedKey = key.replaceAll("*", ".");
      const matchingField = flattenedCustomFields.find(
        (field) => field.encompass_ids[0].id === untransformedKey
      );
      return {
        fieldId: untransformedKey,
        value: customFieldValues[key],
        description: matchingField?.label || key,
        dataType: matchingField.encompass_ids[0].name
      };
    });
    formData.lockRequestAdditionalFields = lockRequestAdditionalFields;
    return formData;
  }
  return formData;
};
const deriveAccordionFieldMapFromSchema = (schema) => {
  const map = {};
  const screens = schema?.schema?.screens?.[0]?.screens || [];
  screens.forEach((screen, index) => {
    const detailedFields = schema.schema.screens?.find((s) => s.id === screen.id)?.fields?.find((f) => f.name === "DETAILED")?.fields || [];
    map[index] = detailedFields.map(
      (f) => f?.encompass_ids?.[0]?.modelPath
    );
  });
  return map;
};
const expandAccordionsByErrorPaths = (accordionFieldMap, errorPaths) => {
  return Object.entries(accordionFieldMap).reduce((acc, [index, paths]) => {
    if (paths.some((p) => errorPaths.includes(p))) {
      acc[+index] = true;
    }
    return acc;
  }, {});
};
const expandAllAccordions = (accordionFieldMap) => {
  return Object.entries(accordionFieldMap).reduce((acc, [index, paths]) => {
    if (paths.length > 0) {
      acc[+index] = true;
    }
    return acc;
  }, {});
};
const scrollToFirstError = (errorPaths, delayMs = 100) => {
  setTimeout(() => {
    const el = document.getElementById(errorPaths[0]);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    el?.focus();
  }, delayMs);
};
export {
  deriveAccordionFieldMapFromSchema,
  expandAccordionsByErrorPaths,
  expandAllAccordions,
  scrollToFirstError,
  transformFormDataCustomFieldsToResponseFormData,
  validateAndSubmitSearchForm
};
